import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowRight, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BUSINESS, SERVICES } from "@/data/content";

const initial = { name: "", email: "", phone: "", service: "General enquiry", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in name, email, phone and a short message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thanks! George will be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-black/20 py-3 text-concrete-900 placeholder:text-concrete-400 focus:outline-none focus:border-concrete-900 transition-colors";

  return (
    <section id="contact" className="bg-concrete-50 py-24 sm:py-32 border-t border-black/10">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Left: info */}
        <div>
          <span className="overline text-concrete-500">/ Enquire</span>
          <h2 className="mt-4 font-display font-extrabold tracking-tighter text-concrete-900 text-5xl sm:text-6xl leading-[0.95]">
            Let's talk<br />concrete.
          </h2>
          <p className="mt-6 max-w-md text-concrete-600 leading-relaxed">
            Tell us about the job and we'll come back with an honest quote. Prefer to chat? Call {BUSINESS.owner} direct.
          </p>

          <div className="mt-10 space-y-1 border-t border-black/10">
            <a href={`tel:${BUSINESS.phoneIntl}`} data-testid="contact-phone" className="group flex items-center gap-4 py-5 border-b border-black/10">
              <Phone className="h-5 w-5 text-concrete-500 group-hover:text-concrete-900 transition-colors" />
              <span className="font-display font-semibold text-xl text-concrete-900">{BUSINESS.phone}</span>
            </a>
            <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="group flex items-center gap-4 py-5 border-b border-black/10">
              <Mail className="h-5 w-5 text-concrete-500 group-hover:text-concrete-900 transition-colors" />
              <span className="text-lg text-concrete-900 break-all">{BUSINESS.email}</span>
            </a>
            <div className="flex items-center gap-4 py-5 border-b border-black/10">
              <MapPin className="h-5 w-5 text-concrete-500" />
              <span className="text-lg text-concrete-900">{BUSINESS.area}</span>
            </div>
            <div className="flex items-center gap-4 py-5 border-b border-black/10">
              <Clock className="h-5 w-5 text-concrete-500" />
              <span className="text-lg text-concrete-900">Always open — 7 days</span>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <form onSubmit={submit} data-testid="enquiry-form" className="bg-white border border-black/10 p-7 sm:p-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="overline text-concrete-500">Name</label>
              <input data-testid="enquiry-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" />
            </div>
            <div>
              <label className="overline text-concrete-500">Phone</label>
              <input data-testid="enquiry-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="04xx xxx xxx" />
            </div>
          </div>
          <div className="mt-6">
            <label className="overline text-concrete-500">Email</label>
            <input data-testid="enquiry-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
          </div>
          <div className="mt-6">
            <label className="overline text-concrete-500">Service</label>
            <select data-testid="enquiry-service" value={form.service} onChange={update("service")} className={`${inputCls} cursor-pointer`}>
              <option>General enquiry</option>
              {SERVICES.map((s) => (
                <option key={s.id}>{s.title}</option>
              ))}
            </select>
          </div>
          <div className="mt-6">
            <label className="overline text-concrete-500">Job details</label>
            <textarea data-testid="enquiry-message" value={form.message} onChange={update("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Tell us what you need, size, location…" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="enquiry-submit"
            className="mt-8 group w-full flex items-center justify-center gap-3 bg-concrete-900 text-white px-6 py-4 text-sm font-semibold uppercase tracking-wide border border-concrete-900 hover:bg-white hover:text-concrete-900 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send enquiry <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></>}
          </button>
        </form>
      </div>
    </section>
  );
}
